/**
 * The study's game view: the same kitchen, HUD, keys, voice and recording as
 * the friends-mode room page, in a room the server opened and drives.
 *
 * What is different is what is missing. There is no invite code, no AI seat
 * button, no lobby tutorial and — the one that matters — no "Play again": when
 * a game ends the server says which game is next and when it starts, and this
 * page shows that countdown instead of the end-of-round card.
 *
 * Readiness works as it does in a friends room for game 1 (wait for voice, up
 * to `voiceReadyGraceMs`, then say ready); games 2 and 3 are started by the
 * server, and a `ready` sent then is harmless.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ClientConfig, IceConfig, PlayerId } from '@overcooked/shared'
import type { EventFeed } from '../feed/feed'
import type { Connection, RoomInfo } from '../net/socket'
import { buildCatalog } from '../render/catalog'
import type { RecipesControl } from '../render/RecipePanel'
import { Stage } from '../render/Stage'
import { seatIdentity } from '../render/theme'
import { usePeerVoice } from '../voice/usePeerVoice'
import { prettyMapName } from '../flow/display'
import { KEY_TO_DIRECTION, isSystemChord, roundKeysActive } from '../flow/keys'
import { useRoundOutcome, useSessionRecording, useVoiceStateReport } from '../flow/sessionHooks'
import type { BetweenGames } from './betweenGames'
import { betweenSecondsLeft, headerGameIndex } from './betweenGames'

export type { BetweenGames } from './betweenGames'

interface StudyGameProps {
  connection: Connection
  feed: EventFeed
  room: RoomInfo
  config: ClientConfig | null
  micStream: MediaStream | null
  ice: IceConfig | null
  gameIndex: number
  games: number
  countdown: number | null
  between: BetweenGames | null
}

const VOICE_LABEL: Record<string, string> = {
  idle: 'waiting for your partner',
  connecting: 'connecting…',
  connected: 'connected',
  reconnecting: 'reconnecting…',
  failed: 'not working',
}

export function StudyGame({
  connection,
  feed,
  room,
  config,
  micStream,
  ice,
  gameIndex,
  games,
  countdown,
  between,
}: StudyGameProps) {
  const connectionRef = useRef<Connection | null>(connection)
  connectionRef.current = connection
  const { ended, tally } = useRoundOutcome(feed)
  const catalog = useMemo(() => buildCatalog(config), [config])
  useSessionRecording(feed, micStream, room.phase, room.sessionId, connectionRef)

  useEffect(() => {
    feed.setSelfId(room.playerId)
  }, [feed, room.playerId])

  // "Through the gates", once per seat: a reconnect re-seats and says it again.
  const setupFor = useRef<PlayerId | null>(null)
  useEffect(() => {
    if (!room.playerId || setupFor.current === room.playerId) return
    setupFor.current = room.playerId
    connection.setupDone()
  }, [room.playerId, connection])

  const peer = room.players.find((p) => p.id !== room.playerId) ?? null
  const sendSignal = useCallback<NonNullable<Connection['sendSignal']>>((to, signal) => connectionRef.current?.sendSignal(to, signal), [])
  const onSignal = useCallback<Connection['onSignal']>((handler) => connectionRef.current?.onSignal(handler) ?? (() => undefined), [])
  const requestCredentials = useCallback(() => connectionRef.current?.requestCredentials(), [])
  const onRttSample = useCallback((rttMs: number) => connectionRef.current?.reportRtt(rttMs), [])
  const voice = usePeerVoice({
    localStream: micStream,
    selfId: room.playerId,
    peerId: peer?.id ?? null,
    ice,
    sendSignal,
    onSignal,
    requestCredentials,
    sampleIntervalMs: config?.latency.voiceSampleIntervalMs ?? 5000,
    onRttSample,
  })
  useVoiceStateReport(connectionRef, voice.status, voice.relayed, room.phase)

  const self = room.players.find((p) => p.id === room.playerId)
  const iAmReady = self?.ready ?? false

  // Ready once voice has settled, or after the grace — the friends-mode rule.
  useEffect(() => {
    if (room.phase !== 'lobby' || !room.playerId || !config || iAmReady) return
    if (voice.status === 'connected' || voice.status === 'failed') {
      connection.ready()
      return
    }
    const timer = setTimeout(() => connectionRef.current?.ready(), config.voiceReadyGraceMs)
    return () => clearTimeout(timer)
  }, [room.phase, room.playerId, iAmReady, config, voice.status, connection])

  const [recipesOpen, setRecipesOpen] = useState(false)
  const recipesOpenRef = useRef(false)
  recipesOpenRef.current = recipesOpen
  const orderPool = useMemo(
    () => config?.maps.find((map) => map.name === room.mapName)?.orderPool ?? [],
    [config, room.mapName],
  )
  const recipes = useMemo<RecipesControl>(
    () => ({
      open: recipesOpen,
      pool: orderPool,
      toggle: () => setRecipesOpen((open) => !open),
      close: () => setRecipesOpen(false),
    }),
    [recipesOpen, orderPool],
  )

  // The round's keys — the same bindings and the same guards as the room page.
  useEffect(() => {
    if (!roundKeysActive({ stage: 'playing', phase: room.phase, ended: Boolean(ended) })) return
    const onKeyDown = (event: KeyboardEvent): void => {
      const conn = connectionRef.current
      if (!conn || isSystemChord(event)) return
      const direction = KEY_TO_DIRECTION[event.key]
      if (direction) {
        event.preventDefault()
        if (recipesOpenRef.current) setRecipesOpen(false)
        conn.move(direction)
        return
      }
      if (event.key === ' ') {
        event.preventDefault()
        if (!event.repeat) conn.interact()
        return
      }
      if (event.key === 'r' || event.key === 'R') {
        event.preventDefault()
        if (!event.repeat) setRecipesOpen((open) => !open)
        return
      }
      if (event.key === 'Escape' && recipesOpenRef.current) {
        event.preventDefault()
        setRecipesOpen(false)
      }
    }
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === ' ') connectionRef.current?.interactRelease()
    }
    const onBlur = (): void => connectionRef.current?.interactRelease()
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
      connectionRef.current?.interactRelease()
    }
  }, [room.phase, ended])

  // Reset as soon as a pause starts: a `now` left over from the last render
  // before it would read as a countdown far longer than the pause.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!between) return
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(timer)
  }, [between])

  const partnerAway = peer !== null && !peer.connected
  const seat = self?.seat

  return (
    <main className="page">
      <header className="bar">
        <span className="brand">
          Over<em>cooked</em>
        </span>
        <span className="code">
          Round {headerGameIndex(gameIndex, games, between)} of {games}
        </span>
        {room.mapName && <span className="code">{prettyMapName(room.mapName)}</span>}
        {seat && <span>You're the {seatIdentity(seat).label.toLowerCase()} chef</span>}
      </header>

      {partnerAway && (
        <p className="notice" role="status">
          Your partner lost connection. We'll wait a minute for them to come back.
        </p>
      )}

      {room.phase === 'lobby' && !between && (
        <section className="lobby">
          {/* "Round N of 3" is already in the header bar above. */}
          <h2>{countdown !== null ? 'Get ready…' : peer ? 'Connecting voice…' : 'Setting up your kitchen…'}</h2>
          {countdown !== null && (
            <p className="seat-countdown" role="status" aria-live="assertive">
              <span key={countdown} className="seat-countdown-value">
                {countdown}
              </span>
              <span className="seat-countdown-cap">until the round starts</span>
            </p>
          )}
          <p className="hint">You'll talk to your partner by voice. Say hello!</p>
        </section>
      )}

      {(between || (ended && room.phase !== 'playing')) && (
        <section className="gate study-between" role="status" aria-live="polite">
          <p className="gate-step">
            Round {between?.completedGameIndex ?? gameIndex} of {games} done
          </p>
          <h1>
            {between
              ? `Next round in ${betweenSecondsLeft(between, Math.max(now, Date.now()))}…`
              : gameIndex >= games
                ? 'All rounds played'
                : 'Well played'}
          </h1>
          {ended && (
            <p className="hint">
              {ended.score} point{Math.abs(ended.score) === 1 ? '' : 's'} · {tally.matched} order{tally.matched === 1 ? '' : 's'} served
            </p>
          )}
          {between?.replay && <p className="hint">No orders were served, so you'll play this kitchen again.</p>}
        </section>
      )}

      {room.phase === 'playing' && !ended && (
        <Stage feed={feed} catalog={catalog} selfId={room.playerId} showHud recipes={recipes} />
      )}

      <p className="hint voice-status">
        <span className={`dot dot-${voice.status}`} aria-hidden="true" />
        Voice: {VOICE_LABEL[voice.status]}
      </p>
      {voice.error && <p className="error">{voice.error}</p>}
    </main>
  )
}
